import useFetch from "../../hooks/useFetch";
import { getActiveSubscriptionPlans } from "../../services/subscriptionPlan";
import {
  cancelSubscription,
  createSubscription,
  renewSubscription,
  upgradeSubscription,
} from "../../services/subscription";
import useAuth from "../../hooks/useAuth";
import useCustomUpload from "../../hooks/useUpload";
import useModal from "../../hooks/useModal";
import { useState } from "react";
import { ISubscriptionPlan } from "../../types/interfaces";
import { message } from "antd";
import { deleteFile } from "../../services/files";
import useLoading from "../../hooks/useLoading";
import { AxiosError } from "axios";

function useSubscriptions() {
  const { data, error, loading, total, refetch } = useFetch({
    service: getActiveSubscriptionPlans,
  });

  const [selectedSubscription, setSelectedSubscription] =
    useState<ISubscriptionPlan | null>(null);
  const [isRenew, setIsRenew] = useState(false);

  const receiptModal = useModal();

  const { cleanUploads, handleUpload, handleFileChange, uploads } =
    useCustomUpload();

  const { refreshUserData, subscriptionPlan, subscription } = useAuth();

  const subscriptionLoading = useLoading();

  const handleCancel = () => {
    cleanUploads();
    setSelectedSubscription(null);
    setIsRenew(false);
    receiptModal.closeModal();
  };

  const handleSelect = (id: number, renew?: boolean) => {
    let item = data.find((item: ISubscriptionPlan) => item.id === id);
    setSelectedSubscription(item);
    if (renew) {
      setIsRenew(true);
    }
    receiptModal.openModal();
  };

  const handleCancelSubscription = async () => {
    try {
      subscriptionLoading.start();
      await cancelSubscription();
      await refreshUserData();
      refetch();
    } catch (error: Error | any) {
      console.log("error", error);
    } finally {
      subscriptionLoading.stop();
    }
  };

  const handleOk = async () => {
    if (!selectedSubscription) return;
    subscriptionLoading.start();
    let upload: any = null;
    try {
      upload = await handleUpload({
        name: "receipt",
        category: "subscriptions",
        required: true,
      });

      if (isRenew) {
        await renewSubscription({
          id: subscription?.id,
          receiptId: upload.path || "",
        });
      } else if (
        subscription?.active &&
        selectedSubscription?.id !== subscriptionPlan?.id
      ) {
        await upgradeSubscription({
          planId: selectedSubscription.id,
          receiptId: upload.path || "",
        });
      } else {
        await createSubscription({
          planId: selectedSubscription.id,
          receiptId: upload.path || "",
        });
      }
      await refreshUserData();
      refetch();
      cleanUploads();
      receiptModal.closeModal();
      message.success("Te has suscrito correctamente");
    } catch (error: AxiosError | Error | any) {
      console.log("error", error);
      if (error?.response?.data?.trim?.() === "subscription already exists") {
        message.warning("Ya tienes esta suscripción");
      } else if (error?.message === "El archivo es requerido.") {
        message.warning("Antes de continuar debes adjuntar el recibo.");
      } else if (error?.response?.data) {
        message.error(`Error: ${error.response.data}`);
      } else {
        message.error("Ocurrió un error al procesar tu solicitud.");
      }
      
      if (upload?.path) {
        await deleteFile(upload.path).catch(() => {});
      }
    } finally {
      subscriptionLoading.stop();
    }
  };

  return {
    data,
    error,
    handleCancel,
    handleCancelSubscription,
    handleFileChange,
    handleOk,
    handleSelect,
    loading,
    receiptModal,
    selectedSubscription,
    subscriptionLoading,
    subscriptionPlan: subscriptionPlan || null,
    subscription: subscription || null,
    total,
    uploads,
  };
}

export default useSubscriptions;
